import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Import all sections
import Header from './Header';
import HeroSection from './HeroSection';
import AboutSection from './AboutSection';
import ValuesSection from './ValuesSection';
import ProcessSection from './ProcessSection';
import TechStackSection from './TechStackSection';
import TechnologyInActionSection from './TechnologyInActionSection';
import AwardsSection from './AwardsSection';
import ServicesSection from './ServicesSection';
import ProductsSection from './ProductsSection';
import PortfolioSection from './PortfolioSection';
import TestimonialsSection from './TestimonialsSection';
import TeamSection from './TeamSection';
import InsightsSection from './InsightsSection';
import CareersSection from './CareersSection';
import ContactSection from './ContactSection';
import Footer from './Footer';
import ChatbotWidget from './ChatbotWidget';
import CookieBanner from './CookieBanner';
import LoadingSpinner from './common/LoadingSpinner';

// Import hooks
import { useCompanyInfo } from '../hooks/useAPI';

const HomePage = () => {
  const { data: companyInfo, isLoading, error } = useCompanyInfo();
  const location = useLocation();

  useEffect(() => {
    // Handle scroll to section from navigation state
    if (location.state?.scrollTo) {
      const sectionId = location.state.scrollTo;
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    // Initialize scroll reveal animations
    const revealElements = document.querySelectorAll('.reveal');
    
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    revealElements.forEach(el => revealObserver.observe(el));

    return () => {
      revealElements.forEach(el => revealObserver.unobserve(el));
    };
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    console.error('Error loading company info:', error);
  }

  const defaultCompanyInfo = {
    companyName: 'LionTek',
    tagline: 'Engineering Global Solutions. Powering The Future.',
    description: 'LionTek is a world-class technology firm leveraging AI, enterprise software, and cybersecurity to drive innovation for businesses worldwide.',
    email: 'info@liontek.com',
    phone: '+232 XX XXX XXXX',
    address: 'Freetown, Sierra Leone',
    socialMedia: {
      twitter: '',
      linkedin: '',
      facebook: '',
      instagram: ''
    }
  };

  const company = companyInfo || defaultCompanyInfo;

  return (
    <>
      <Helmet>
        <title>{company.companyName} - Engineering Global Solutions</title>
        <meta name="description" content={company.description} />
        <meta name="keywords" content="technology, AI, cybersecurity, ERP, software development, Sierra Leone, Africa" />
        <meta property="og:title" content={`${company.companyName} - Engineering Global Solutions`} />
        <meta property="og:description" content={company.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://liontekglobal.com" />
        <link rel="canonical" href="https://liontekglobal.com" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              '@id': 'https://liontekglobal.com/#organization',
              name: company.companyName,
              url: 'https://liontekglobal.com',
              logo: { '@type': 'ImageObject', url: 'https://liontekglobal.com/logo.svg' },
              contactPoint: { '@type': 'ContactPoint', email: company.email, contactType: 'customer service' },
              address: { '@type': 'PostalAddress', addressLocality: 'Freetown', addressCountry: 'SL' },
              sameAs: Object.values(company.socialMedia || {}).filter(Boolean),
            },
            {
              '@type': 'WebSite',
              '@id': 'https://liontekglobal.com/#website',
              name: company.companyName,
              url: 'https://liontekglobal.com',
              publisher: { '@id': 'https://liontekglobal.com/#organization' },
            },
          ],
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-white font-sans text-gray-800">
        {/* Header & Navigation */}
        <Header companyInfo={company} />

        {/* Main Content */}
        <main>
          {/* Hero Section */}
          <HeroSection companyInfo={company} />

          {/* About Us Section */}
          <AboutSection companyInfo={company} />

          {/* Values Section */}
          <ValuesSection />

          {/* Process Section */}
          <ProcessSection />

          {/* Tech Stack Section */}
          <TechStackSection />

          {/* Technology in Action Section */}
          <TechnologyInActionSection />

          {/* Awards Section */}
          <AwardsSection />

          {/* Services Section */}
          <ServicesSection />

          {/* Products Section */}
          <ProductsSection />

          {/* Portfolio Section */}
          <PortfolioSection />

          {/* Testimonials Section */}
          <TestimonialsSection />

          {/* Team Section */}
          <TeamSection />

          {/* Insights Section */}
          <InsightsSection />

          {/* Careers Section */}
          <CareersSection />

          {/* Contact Section */}
          <ContactSection companyInfo={company} />
        </main>

        {/* Footer */}
        <Footer companyInfo={company} />

        {/* Chatbot Widget */}
        <ChatbotWidget />

        {/* Cookie Banner */}
        <CookieBanner />
      </div>
    </>
  );
};

export default HomePage;